import {Separator} from 'platform/components';
import {HStack, Show, Spinner, Text} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {formatTimeDifference} from '../utils/formatTimeDifferenceToNow';

interface LiveLastUpdateTimeProps {
  lastUpdateTime?: number | null;
  isLiveUpdateEnabled?: boolean;
  isPolling?: boolean;
}

export function LiveLastUpdateTime(props: LiveLastUpdateTimeProps) {
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const {lastUpdateTime} = props;
  const hasLastUpdateTime = isNotNil(lastUpdateTime);

  // Direct useEffect is intentional: this interval is conditional and must be
  // cleaned up when the timestamp disappears or the component unmounts.
  useEffect(
    function subscribeToRelativeTimeUpdates() {
      if (!hasLastUpdateTime) {
        return;
      }

      const intervalId = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    },
    [hasLastUpdateTime]
  );

  const formattedTime = isNotNil(lastUpdateTime)
    ? formatTimeDifference(lastUpdateTime, Math.max(currentTime, lastUpdateTime), true)
    : null;

  const statusLabel = isNotNil(formattedTime)
    ? i18n.t('entity.sourcingTable.labels.lastUpdate')
    : i18n.t('entity.sourcingTable.labels.realTime');
  const statusValue = formattedTime ?? i18n.t('general.labels.enabled');

  return (
    <Show
      when={props.isLiveUpdateEnabled}
      whenFeatureEnabled={featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT}
    >
      <Separator orientation="vertical" spacing={0} />
      <HStack align="center" spacing={1}>
        <Text size="xSmall" color="secondary">
          {statusLabel}:
        </Text>
        <Text size="xSmall" color="secondary" alternative>
          {statusValue}
        </Text>
        <Show when={props.isPolling}>
          <Spinner size="xSmall" />
        </Show>
      </HStack>
    </Show>
  );
}
